/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {useThemeContext} from './useThemeContext';

// provides a helper function for generating the component name ...
const getDisplayName = (Component: React.ComponentType, fallback: string) => (
  Component.displayName || Component.name || `${fallback}Component`
);

/**
 * Higher-order component that conditionally renders different component
 * implementations based on the currently selected theme, passing down all
 * props accordingly.
 *
 * Useful for cases where different component implementations are needed for
 * different themes.
 *
 * The following is a basic example which simply exports a new component which
 * defines the Legacy and Current component implementations:
 *
 * ```typescript
 *   import {withDLSTheme} from '@ion/dls/src/theme/withDLSTheme';
 *   import LegacyComponent from './LegacyComponent';
 *   import CurrentComponent from './CurrentComponent';
 *
 *   export default withDLSTheme(LegacyComponent, CurrentComponent);
 * ```
 *
 * For more complex scenarios where the theme-aware component needs to be wrapped
 * with additional styling, layout, or state, create the theme-aware component
 * first, then wrap it with your outer component:
 *
 * ```typescript
 *   import LegacyComponent, {LegacyComponentProps} from './LegacyComponent';
 *   import CurrentComponent, {CurrentComponentProps} from './CurrentComponent';
 *
 *   type Props = LegacyComponentProps & CurrentComponentProps;
 *
 *   // create the theme-aware component ...
 *   const ThemeAwareComponent = withDLSTheme(LegacyComponent, CurrentComponent);
 *
 *   // wrap with outer component ...
 *   const SomeComponent = (props: Props) => (
 *     <div style={classes.container}>
 *       <ThemeAwareComponent {...props} />
 *     </div>
 *   );
 * ```
 *
 * @param   `LegacyComponent` The component to render when theme is `legacy`.
 * @param   `CurrentComponent` The component to render when theme is `current`.
 * @returns The wrapped component with conditional rendering based on theme.
 */
export const withDLSTheme = <P extends object>(
  LegacyComponent: React.ComponentType<P>,
  CurrentComponent: React.ComponentType<P>
): React.ComponentType<P> => {
  const WrappedComponent = (props: P) => {
    const {isCurrent} = useThemeContext();
    const Component = isCurrent ? CurrentComponent : LegacyComponent;
    return <Component {...props} />;
  };
  WrappedComponent.displayName = (
    `withDLSTheme(${getDisplayName(LegacyComponent, 'Legacy')}, ${getDisplayName(CurrentComponent, 'Current')})`
  );
  return WrappedComponent;
};
